import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Col, Icon, Row, Tooltip, Checkbox, Popover } from "antd";

import useTimeout from "../../hooks/custom/useTimeout";
import { ConditionalWrapper } from "../ConditionalWrapper";

export const SELECT_ALL_STATUS = {
  UNCHEKED: "UNCHECKED",
  INDETERMINATE: "INDETERMINATE",
  CHECKED: "CHECKED"
};

const ActionsSnackbarContainer = styled.div`
  align-items: center;
  background-color: #343a40;
  border-radius: 8px;
  bottom: 8px;
  box-shadow: 0 3px 12px 0 rgba(0, 0, 0, 0.16);
  display: ${({ displayed }) => (displayed ? "flex" : "none")};
  justify-content: space-between;
  position: fixed;
  padding: 12px 18px 12px 18px;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transform: translateY(${({ visible }) => (visible ? 0 : 16)}px);
  transition: opacity 0.4s ease, transform 0.4s ease 0.05s;
  width: 90%;
  left: 5%;
  /* TODO Revise */
  z-index: 9;
`;

const ActionButtonContainer = styled.button`
  appearance: none;
  background-color: transparent;
  border: none;
  border-radius: 50%;
  cursor: ${({ isDisabled }) => (isDisabled ? "not-allowed" : "pointer")};
  outline-color: ${({ color }) => color};
  padding: 8px;
  transition: background-color 0.4s ease;
  :focus,
  :hover {
    background-color: ${({ color, isDisabled }) =>
      isDisabled ? "transparent" : `${color}33`};
    ${({ isDisabled }) => (isDisabled ? "outline: none;" : "")};
  }
  .anticon {
    color: ${({ color }) => color};
    font-size: 24px;
    pointer-events: none;
  }
`;

const TextContainer = styled.div`
  width: 200px;
  display: flex;
  align-items: center;
  margin-left: 26px;
  margin-right: 50px;
`;

const ActionsDescription = styled.span`
  color: #f7f9fa;
  display: inline;
  font-size: 12px;
  margin-left: 10px;
`;

const ActionsTitle = styled.h1`
  color: #f7f9fa;
  display: inline-block;
  font-size: 20px;
  margin: 0;
  width: 24px; /* Add width to prevent moving description text */
  text-align: center;
`;

const LeftSide = styled.div`
  align-items: center;
  display: flex;
`;

const SelectAllCheckbox = styled(Checkbox)`
  color: #fff !important;
  font-size: 12px !important;
`;

// TODO add tooltip with description here?
// We spread the icon in case we want a custom icon component
const ActionButton = ({
  color,
  description,
  enabled = true,
  disabledLabel,
  icon,
  onClick,
  popover,
  ...props
}) => {
  const ref = useRef();
  const [focused, setFocused] = useState(false);
  const blurButton = () => ref.current.blur();
  const hideTooltip = () => setFocused(false);
  const handleClick = event => {
    blurButton();
    onClick && onClick(event);
  };
  const showTooltip = () => setFocused(true);
  return (
    <ConditionalWrapper
      condition={description}
      trueWrapper={children => (
        <Tooltip visible={focused} title={description}>
          {children}
        </Tooltip>
      )}
    >
      <ConditionalWrapper
        condition={popover && enabled}
        trueWrapper={children => (
          <Popover
            placement="top"
            trigger="click"
            destroyTooltipOnHide={true}
            content={popover}
          >
            {children}
          </Popover>
        )}
      >
        <ActionButtonContainer
          {...props}
          aria-label={`${description || "action button"} ${
            enabled ? "" : disabledLabel || "disabled"
          }`}
          color={color}
          isDisabled={!enabled}
          data-test-disabled={Boolean(!enabled)}
          onBlur={hideTooltip}
          onClick={enabled ? handleClick : undefined}
          onFocus={showTooltip}
          onMouseOut={hideTooltip}
          onMouseOver={showTooltip}
          ref={ref}
        >
          <Icon {...icon} />
        </ActionButtonContainer>
      </ConditionalWrapper>
    </ConditionalWrapper>
  );
};

function ActionsSnackbar({
  actions,
  actionsDescription,
  actionsTitle,
  onClose,
  visible,
  onSelectAll,
  selectAllStatus
}) {
  const { t } = useTranslation();

  // To run the first animation when the snackbar is visible we need it to always be invisible
  const [isVisible, setIsVisible] = useState(false);
  // Whenever the snackbar is invisible, we should not display it
  const [isDisplayed, setIsDisplayed] = useState(visible);

  // We use these timeouts for animations
  // When the snackbar animation starts, set a timeout before setting display: none; that runs 100ms after that.
  useTimeout(
    () => setIsDisplayed(false),
    !isVisible && isDisplayed ? 500 : null
  );

  // When the snackbar is visible, set a timeout after displaying it to show the animation (that runs 100ms after it's displayed).
  useTimeout(() => setIsVisible(true), visible && isDisplayed ? 100 : null);

  useEffect(() => {
    if (!isDisplayed && visible) setIsDisplayed(true);
    if (isVisible && !visible) setIsVisible(false);
  }, [isDisplayed, isVisible, visible]);

  return (
    <ActionsSnackbarContainer
      data-test="actions-snackbar"
      displayed={isDisplayed}
      visible={isVisible}
    >
      <LeftSide>
        <ActionButton
          data-test="actions-snackbar-close"
          color="#9ca8b1"
          icon={{ type: "close" }}
          onClick={onClose}
        >
          <Icon type="close" />
        </ActionButton>
        {(actionsTitle || actionsDescription) && (
          <TextContainer>
            {actionsTitle && (
              <ActionsTitle data-test="actions-snackbar-title">
                {actionsTitle}
              </ActionsTitle>
            )}
            {actionsDescription && (
              <ActionsDescription>{actionsDescription}</ActionsDescription>
            )}
          </TextContainer>
        )}
        <SelectAllCheckbox
          data-test={`select-all-candidates-checkbox-${selectAllStatus}`}
          onChange={onSelectAll}
          indeterminate={selectAllStatus === SELECT_ALL_STATUS.INDETERMINATE}
          checked={selectAllStatus === SELECT_ALL_STATUS.CHECKED}
        >
          {t("opportunity:candidates.multipleSelection.selectAll")}
        </SelectAllCheckbox>
      </LeftSide>
      <div>
        {actions && (
          <Row type="flex" gutter={[16, 0]}>
            {actions.map(action => (
              <Col key={action.description}>
                <ActionButton
                  {...action}
                  color={action.enabled ? "#ffffff" : "#747c84"}
                />
              </Col>
            ))}
          </Row>
        )}
      </div>
    </ActionsSnackbarContainer>
  );
}

export default ActionsSnackbar;
